import _ from "lodash";
import React, { useCallback } from "react";
import { useI18n } from "Hooks";

import { Button, Modal, Tooltip } from "@toddle-edu/ds-web";

import classes from "./AddRecipientModal.scss";
import classNames from "classnames";

import { ENTITY_TYPE } from "MyCalendar/modules/MyCalendarModule/MyCalendarConstants";
import { MAP_ENTITY_TYPE_TO_LABEL } from "../../../modules/Utils";

// Child components
import GradeSelectionList from "./components/GradeSelectionList";
import ClassSelectionList from "./components/ClassSelectionList";
import CustomSelectionList from "./components/CustomSelectionList";

const modalBodyStyles = {
  padding: "0",
  overflow: "hidden",
};

const modalFooterStyles = {
  padding: "16px 24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const footerSubtextClasses = classNames("text-label", classes.footerSubtext);

const FooterSelectCount = ({ participantSelections, selectedListType }) => {
  const { t } = useI18n();

  const selectedCount = _.size(participantSelections[selectedListType]);

  const getCountText = () => {
    switch (selectedListType) {
      case ENTITY_TYPE.GRADES:
        return t("common:grade_selected_with_count", { count: selectedCount });
      case ENTITY_TYPE.CLASSES:
        return t("common:selected_course_count", { count: selectedCount });
      case ENTITY_TYPE.CUSTOM:
        return t("common:count_member_selected_other", {
          count: selectedCount,
        });
      default:
        return `${selectedCount} selected`;
    }
  };

  return <div className={classes.footerLeft}>{getCountText()}</div>;
};

const ModalBody = ({
  sharedWith,
  selectedEntityType,
  selectedRecipientList,
  setSelectedRecipientList,
}) => {
  // Had to add callback support to this as the child component expect it to be a state setter function
  const setSelectedGradeList = useCallback(
    newGradeListOrCallback => {
      setSelectedRecipientList(prevSelections => {
        const newGradeList = _.isFunction(newGradeListOrCallback)
          ? newGradeListOrCallback(prevSelections[ENTITY_TYPE.GRADES])
          : newGradeListOrCallback;

        return {
          ...prevSelections,
          [ENTITY_TYPE.GRADES]: newGradeList,
        };
      });
    },
    [setSelectedRecipientList]
  );

  const setSelectedClassList = useCallback(
    newClassListOrCallback => {
      setSelectedRecipientList(prevSelections => {
        const newClassList = _.isFunction(newClassListOrCallback)
          ? newClassListOrCallback(prevSelections[ENTITY_TYPE.CLASSES])
          : newClassListOrCallback;

        return {
          ...prevSelections,
          [ENTITY_TYPE.CLASSES]: newClassList,
        };
      });
    },
    [setSelectedRecipientList]
  );

  switch (selectedEntityType) {
    case ENTITY_TYPE.CLASSES:
      return (
        <ClassSelectionList
          selectedCourseList={selectedRecipientList?.[ENTITY_TYPE.CLASSES]}
          setSelectedCourseList={setSelectedClassList}
          selectedUserTypes={sharedWith}
        />
      );
    case ENTITY_TYPE.GRADES:
      return (
        <GradeSelectionList
          selectedGradeList={selectedRecipientList?.[ENTITY_TYPE.GRADES]}
          setSelectedGradeList={setSelectedGradeList}
          selectedUserTypes={sharedWith}
        />
      );
  }
};

const AddRecipientModal = props => {
  const {
    sharedWith,
    entityType,
    updateFormValues,
    isOpen,
    onClose,
    userGroupLabel,
    isAllUserGroupsSelected,
    isRecipientSelected,
    showFooterSubtext = true,
    testId,
    participantSelections,
    setParticipantSelections,
    totalParticipants,
    currentCurriculumProgramId,
    eventModalV2,
  } = props;
  const componentTestId = testId
    ? `${testId}-addRecipientModal`
    : "addRecipientModal";
  const { t } = useI18n();

  const handleRemoveAll = () => {
    setParticipantSelections([]);
  };

  const handleCloseModal = () => {
    setParticipantSelections(eventModalV2?.participants);
    onClose();
  };

  const handleAddRecipients = () => {
    updateFormValues({
      participantSelections,
    });
    onClose();
  };

  const getModalTitle = () => {
    if (entityType === ENTITY_TYPE.CUSTOM) {
      return t("calendar:select_event_participants");
    }

    return t("calendar:select_entity_to_invite_participants", {
      entity: t(MAP_ENTITY_TYPE_TO_LABEL[entityType]),
    });
  };

  return (
    <Modal
      dsVersion="2.0"
      isOpen={isOpen}
      onClose={handleCloseModal}
      width={entityType === ENTITY_TYPE.CUSTOM ? "1000px" : "544px"}
      height="680px"
    >
      <Modal.Header
        hasSeparator={entityType === ENTITY_TYPE.CUSTOM}
        title={getModalTitle()}
      />
      <Modal.Body style={modalBodyStyles}>
        {entityType === ENTITY_TYPE.CUSTOM ? (
          <CustomSelectionList
            setSelectedRecipientList={setParticipantSelections}
            sharedWith={sharedWith}
            selectedCustomList={participantSelections?.[ENTITY_TYPE.CUSTOM]}
            currentCurriculumProgramId={currentCurriculumProgramId}
          />
        ) : (
          <ModalBody
            sharedWith={sharedWith}
            selectedEntityType={entityType}
            selectedRecipientList={participantSelections}
            setSelectedRecipientList={setParticipantSelections}
          />
        )}
      </Modal.Body>
      <Modal.Footer hasSeparator style={modalFooterStyles}>
        <div className={footerSubtextClasses}>
          {showFooterSubtext ? (
            <FooterSelectCount
              participantSelections={participantSelections}
              selectedListType={entityType}
            />
          ) : null}
        </div>
        <div className={classes.footerButtonWrapper}>
          {entityType !== ENTITY_TYPE.CUSTOM ? (
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              onClick={handleRemoveAll}
              disabled={_.isEmpty(participantSelections)}
              testId={`${componentTestId}-clearAll`}
            >
              {t("announcement:clear_all")}
            </Button>
          ) : null}
          <Button
            dsVersion="2.0"
            variant="progressive"
            type="fill"
            disabled={!totalParticipants}
            onClick={handleAddRecipients}
            testId={`${componentTestId}-addRecipients`}
          >
            {!totalParticipants ? (
              <Tooltip
                tooltip={t("calendar:participant_count_still_0")}
                placement="top"
                dsVersion="2.0"
                showArrow={true}
              >
                <span>
                  {isRecipientSelected ||
                  _.some(participantSelections, s => !_.isEmpty(s))
                    ? t("common:update")
                    : t("common:select")}
                </span>
              </Tooltip>
            ) : isRecipientSelected ||
              _.some(participantSelections, s => !_.isEmpty(s)) ? (
              t("common:update")
            ) : (
              t("common:select")
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddRecipientModal;
